// Shared precedence for persisted document classifications.
//
// `class` is canonical only when its provenance proves that it came from a
// content-aware classifier, a validated persisted manifest, or an explicit
// human/operator correction. No class decided by a file name is read (CN6):
// File Explorer's old scan-time guess, the preprocessor's
// `metadata.filenameClass` and Stage 1's old `heuristicClassification` are
// all skipped.

const CONTEXT_CLASSIFICATIONS = new Set([
    'our_past_quote',
    'competitor_quote',
    // The business's own scope notes (D6). Context, never the client's
    // request: registering it here keeps an AI "competitor" label or a
    // filename rule from outranking it.
    'estimator_notes',
    'plan',
    'report',
    'assessor_notes',
    'scope_of_works',
]);

const TRUSTED_CLASS_SOURCES = new Set([
    'document-type-classifier',
    'document-type-classifier-content',
    'validated-manifest',
    'operator-assistant-reclassification',
    'operator-reclassification',
    'manual-reclassification',
    'human-reclassification',
    'user-confirmed',
]);

function normaliseClassification(value: any) {
    if (typeof value !== 'string') return null;
    const normalised = value.trim().toLowerCase();
    if (!normalised) return null;
    if (normalised === 'competitor') return 'competitor_quote';
    if (normalised === 'rfq' || normalised === 'other' || CONTEXT_CLASSIFICATIONS.has(normalised)) {
        return normalised;
    }
    return null;
}

/** True when the document resolves to the business's own scope notes (D6). */
export function isEstimatorNotesDocument(document: any) {
    return resolveDocumentClassification(document).classification === 'estimator_notes';
}

/**
 * True when the document resolves to the client's request or a scope of
 * works, the documents a prompt lists first. Decided by the class the content
 * gave the document, never by its file name (CN6: the old order put any
 * document whose name held "rfq", "tender", "quote request" or "scope" first).
 */
export function isRequestOrScopeDocument(document: any) {
    const classification = resolveDocumentClassification(document).classification;
    return classification === 'rfq' || classification === 'scope_of_works';
}

export function documentCanonicalClassSource(document: any) {
    return String(
        document?.classificationSource
        || document?.classSource
        || document?.metadata?.canonicalClassSource
        || '',
    )
        .trim()
        .toLowerCase();
}

export function isOperatorDocumentClassSource(source: any) {
    const normalised = String(source || '').trim().toLowerCase();
    return /(?:^|[-_:])(operator|manual|human|user-confirmed)(?:$|[-_:])/.test(normalised);
}

export function isTrustedDocumentClassSource(source: any) {
    const normalised = String(source || '').trim().toLowerCase();
    return TRUSTED_CLASS_SOURCES.has(normalised) || isOperatorDocumentClassSource(normalised);
}

function directClassificationCandidates(document: any, typeSource = 'persisted-document-type') {
    return [
        {
            classification: normaliseClassification(document?.type),
            source: typeSource,
        },
        {
            classification: normaliseClassification(document?.aiClassification),
            source: 'ai-batch-classifier',
        },
        // CN6: `heuristicClassification` is no longer read. Stage 1 wrote it
        // (until CN4) only as 'scope_of_works', from a file name such as
        // "SOW" or "Scope.pdf" or a loose text rule RJ Bird's own quotes met,
        // and always together with `type`. It outlived `type` only when a
        // later AI label replaced `type`, and then, as a context class, it
        // outranked that label, so a saved request could resolve as a scope
        // of works by its name. Saved data is not rewritten. The field is
        // simply not read.
    ].filter(candidate => candidate.classification);
}

/**
 * Resolve the effective classification and the source that actually won.
 * Context types deliberately veto an RFQ candidate among legacy direct fields
 * (the existing fail-closed gate behaviour), followed by RFQ and then other.
 */
export function resolveDocumentClassification(document: any = {}) {
    const canonicalClass = normaliseClassification(document?.class);
    const canonicalSource = documentCanonicalClassSource(document);
    // Confidence/evidence fields are descriptive legacy metadata, not proof
    // of who produced the classification. Content provenance is granted only
    // by an explicit classSource/classificationSource value.
    const effectiveCanonicalSource = canonicalSource;

    if (canonicalClass && isTrustedDocumentClassSource(effectiveCanonicalSource)) {
        return {
            classification: canonicalClass,
            source: effectiveCanonicalSource,
            basis: 'trusted-canonical-class',
            trustedCanonicalClass: true,
        };
    }

    // An AI batch content label is not trusted canonical authority (so it must
    // not make a stale `class` field win), but it is still the truthful
    // provenance of the `type` value Stage 1 just persisted. Preserve that
    // source on the direct candidate instead of laundering it into
    // "persisted-document-type".
    const typeSource = isTrustedDocumentClassSource(effectiveCanonicalSource)
        || effectiveCanonicalSource === 'ai-batch-classifier-content'
        ? effectiveCanonicalSource
        : 'persisted-document-type';
    const direct = directClassificationCandidates(document, typeSource);
    const context = direct.find(candidate => CONTEXT_CLASSIFICATIONS.has(candidate.classification as string));
    if (context) {
        return {
            ...context,
            basis: 'direct-evidence',
            trustedCanonicalClass: false,
        };
    }
    const rfq = direct.find(candidate => candidate.classification === 'rfq');
    if (rfq) {
        return {
            ...rfq,
            basis: 'direct-evidence',
            trustedCanonicalClass: false,
        };
    }
    const other = direct.find(candidate => candidate.classification === 'other');
    if (other) {
        return {
            ...other,
            basis: 'direct-evidence',
            trustedCanonicalClass: false,
        };
    }

    // Weak capped/error classes remain useful when no stronger persisted type
    // or content result exists, but never receive precedence. A class the
    // File Explorer once guessed from a file name at scan time is not one
    // (CN6): its scan no longer reads names, and a stored guess is skipped.
    if (canonicalClass && effectiveCanonicalSource !== 'scan-filename-only') {
        return {
            classification: canonicalClass,
            source: effectiveCanonicalSource || 'untrusted-canonical-class',
            basis: 'weak-canonical-class',
            trustedCanonicalClass: false,
        };
    }

    // CN6: `metadata.filenameClass` (the preprocessor's guess from the file
    // name) is no longer read. Before CN6 a document with no other class took
    // it, so a file named like a request with no readable text could open the
    // RFQ gate by its name alone.
    return {
        classification: 'other',
        source: 'persisted-document-type',
        basis: 'fallback',
        trustedCanonicalClass: false,
    };
}
